<template>
    <grid ref="grid" :column-defs="columnDefs" :fetch-callback="fetchCallback">
        <div class="d-flex flex-column">
            <b-alert v-if="userIsUnlayerTester" show variant="info" class="mt-2 w-50">
                Old templates are now locked following the deployment of our updated email/document editor
                or because your account is currently transitioning. For all template creation and modification,
                please utilize the new template editor system.
            </b-alert>
            <div class="d-flex align-items-center">
                <responsive-button :icon="['fas', 'arrows-rotate']" @click="refresh" variant="primary">
                    Refresh
                </responsive-button>
                <responsive-button
                    :disabled="userIsUnlayerTester"
                    :icon="['fas', 'plus']"
                    :href="templateAddLink"
                    variant="primary"
                    >Add Template</responsive-button
                >
                <responsive-button
                    :icon="['fas', 'right-left']"
                    class="mr-2"
                    v-if="isImpersonated"
                    variant="outline-primary"
                    @click="$bvModal.show('migration-modal')"
                >
                    Migrate All Templates
                </responsive-button>
                <responsive-button
                    :icon="['fas', 'right-left']"
                    class="mr-2"
                    v-if="isImpersonated"
                    variant="outline-primary"
                    @click="$bvModal.show('migration-triggers-attachments-modal')"
                >
                    Migrate Triggers Attachments
                </responsive-button>
                
            </div>
        </div>
        <modal :id="restoreModalId" title="Restore document" @ok="restoreDocument">
            <p>
                Warning: You are about to restore this document to its original state. All customizations will be lost.
                This cannot be undone. Would you like to proceed?
            </p>
        </modal>
        <modal id="migration-modal" title="Migrate All Templates" @ok="migrateAllTemplates">
            <b-alert show variant="info">
                Only user created and modified system templates will be migrated. Unmodified system templates do not need to be migrated as they are already available in the new editor.
            </b-alert>
            Are you sure you want to migrate all default templates?
        </modal>
        <modal id="migration-triggers-attachments-modal" title="Migrate Triggers Attachments" @ok="migrateTriggersAttachments">
            Are you sure you want to migrate all attachments in triggers?
        </modal>
    </grid>
</template>

<script>
import API from '../../../api';
import {
    DOCUMENT_REPORT_TEMPLATE_CLONE_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_DEFAULT_AGREEMENT_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_GRID_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_HIDE_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_UNHIDE_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_RESTORE_ENDPOINT,
} from '../../../endpoints';
import Grid from '../../generic/grid/grid.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import AdditionalInfoCellRenderer from './additional-info-cell-renderer.vue';
import Modal from '../../generic/modal/modal.vue';
import { mapGetters } from 'vuex';
import { formatResponseError } from '../../../utils/api';

export default {
    name: 'DocumentTemplates',
    components: {
        Grid,
        ResponsiveButton,
        Modal,
        actionsCellRenderer: ActionsCellRenderer,
        additionalInfoCellRenderer: AdditionalInfoCellRenderer,
    },
    data() {
        return {
            restoreModalId: null,
            templateAddLink: '/app/document/0/edit/',
        };
    },
    computed: {
        ...mapGetters('header', ['userIsUnlayerTester', 'isImpersonated']),
        columnDefs() {
            return [
                {
                    field: 'report_name',
                    headerName: 'Name',
                    sort: 'asc',
                },
                {
                    field: 'report_type.name',
                    headerName: 'Type',
                },
                {
                    field: 'report_orientation.name',
                    headerName: 'Orientation',
                },
                ...(this.isImpersonated
                    ? [
                          {
                              field: 'is_migrated',
                              headerName: 'Migrated to Editor',
                              type: 'yesNoColumnNew',
                              minWidth: 180,
                              cellClass: 'text-center',
                              headerClass: 'ag-centered-header',
                          },
                          {
                              field: 'is_changed',
                              headerName: 'Default Template Changed',
                              cellClass: 'text-center',
                              headerClass: 'ag-centered-header',
                              minWidth: 180,
                              cellRenderer: ({ data }) => {
                                  if (data.is_changed) {
                                      return `<span class="badge badge-warning">Changed</span>`;
                                  } else if (data.can_delete) {
                                      return `<span class="badge badge-info">Custom</span>`;
                                  }
                                  return '';
                              },
                          },
                      ]
                    : []),
                {
                    field: 'default_report_type_name',
                    headerName: 'Additional Info',
                    minWidth: 250,
                    cellRenderer: 'additionalInfoCellRenderer',
                    cellRendererParams: {
                        setDefaultAgreement: async (template) => {
                            try {
                                await API.post(DOCUMENT_REPORT_TEMPLATE_DEFAULT_AGREEMENT_ENDPOINT(template.id));
                                this.successToast({
                                    text: `${template.report_name} is now the default rental agreement template.`,
                                });
                            } catch (error) {
                                this.errorToast({ text: `Could not set default rental agreement template.` });
                            }
                            await this.refresh();
                        },
                    },
                },
                {
                    headerName: '',
                    maxWidth: 240,
                    minWidth: 240,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        clone: async (template) => {
                            try {
                                await API.post(DOCUMENT_REPORT_TEMPLATE_CLONE_ENDPOINT(template.id));
                                this.successToast({ text: 'Template successfully cloned.' });
                            } catch (error) {
                                this.errorToast({ text: `Could not clone template.` });
                            }
                            await this.refresh();
                        },
                        delete: async (template) => {
                            try {
                                await API.delete(DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT(template.id));
                            } catch (error) {
                                this.errorToast({ text: `Could not delete template.` });
                            }
                            await this.refresh();
                        },
                        restore: async (template) => {
                            this.restoreModalId = `restore-todo-modal` + template.id;
                            this.$nextTick(() => {
                                this.$bvModal.show(this.restoreModalId);
                            });
                        },
                    },
                },
            ];
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(DOCUMENT_REPORT_TEMPLATE_GRID_ENDPOINT(), {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                    },
                });
            };
        },
    },
    methods: {
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        async restoreDocument() {
            const id = this.restoreModalId.split('restore-todo-modal')[1];
            try {
                await API.post(DOCUMENT_REPORT_TEMPLATE_RESTORE_ENDPOINT(id));
                this.successToast({ text: 'Template successfully restored.' });
            } catch (error) {
                this.errorToast({ text: `Could not restore template.` });
            }
            await this.refresh();
        },
        async migrateAllTemplates() {
            try {
                this.infoToast({
                    text: `Migrating all templates...`,
                });
                await API.post(`${DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT()}migrate-all-templates/`);
                this.successToast({
                    text: `All templates successfully migrated.`,
                });
                this.refresh();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not migrate all templates',
                });
            }
        },
        async migrateTriggersAttachments() {
            try {
                this.infoToast({
                    text: `Migrating triggers attachments...`,
                });
                await API.post(`${DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT()}migrate-triggers-attachments/`);
                this.successToast({
                    text: `Document in triggers successfully migrated.`,
                });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not migrate document in triggers',
                });
            }
        },
    },
};
</script>
